import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, qs, type Paged } from '../api';
import { Btn, Empty, ErrorBox, Loading, Pager, fmtDate, useAction } from '../ui';

interface SynonymGroup {
  id: string;
  words: string[];
  isActive: boolean;
  sortOrder: number;
  multiWord: string[];
  updatedAt: string;
}
interface QueryStat {
  key: string;
  sample: string;
  searches: number;
  zeroResults: number;
  lastAt: string;
}
interface Preview {
  normalized: string;
  tsquery: string | null;
  alternatives: string[][];
}

const splitWords = (s: string) =>
  s
    .split(/[,;\n]/)
    .map((w) => w.trim())
    .filter(Boolean);

/**
 * Поиск: словарь синонимов (рус. ↔ узб. ↔ каракалп., разговорные варианты) и запросы, которые ничего не нашли.
 * Изменения применяются сразу на этом сервере API и в течение минуты на остальных.
 */
export function SearchPage() {
  const qc = useQueryClient();
  const action = useAction();
  const groups = useQuery({ queryKey: ['synonyms'], queryFn: () => api<{ items: SynonymGroup[] }>('/admin/search/synonyms') });
  const [zeroOnly, setZeroOnly] = useState(true);
  const [page, setPage] = useState(1);
  const stats = useQuery({
    queryKey: ['search-queries', zeroOnly, page],
    queryFn: () => api<Paged<QueryStat>>(`/admin/search/queries${qs({ zeroOnly: String(zeroOnly), page, pageSize: 20 })}`),
  });
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState<{ id: string; text: string } | null>(null);
  const [probe, setProbe] = useState('');
  const [preview, setPreview] = useState<Preview | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: ['synonyms'] });

  const add = () =>
    action.run(async () => {
      await api('/admin/search/synonyms', { method: 'POST', json: { words: splitWords(draft) } });
      setDraft('');
      refresh();
    });

  return (
    <div>
      <h1>Поиск</h1>
      <p className="muted">
        Синонимы расширяют запрос: «машина» находит и «mashina», и «avtomobil». Окончания («машины», «telefonlar») срезаются
        автоматически — отдельные формы слов добавлять не нужно. Многословные варианты в расширение не входят.
      </p>
      {action.error ? <ErrorBox error={action.error} /> : null}

      <section className="card form">
        <h2>Проверить запрос</h2>
        <div className="row">
          <input placeholder="например: машины в Нукусе" value={probe} onChange={(e) => setProbe(e.target.value)} />
          <Btn busy={action.busy} disabled={!probe.trim()} onClick={() => void action.run(async () => setPreview(await api<Preview>('/admin/search/preview', { method: 'POST', json: { q: probe } })))}>
            Разобрать
          </Btn>
        </div>
        {preview ? (
          <div className="small" data-testid="search-preview">
            <div>Нормализовано: <code>{preview.normalized || '—'}</code></div>
            {preview.alternatives.length ? (
              <div>
                Ищем:{' '}
                {preview.alternatives.map((alts, i) => (
                  <span key={i}>
                    {i ? ' И ' : ''}(<code>{alts.join(' | ')}</code>)
                  </span>
                ))}
              </div>
            ) : (
              <div>Запрос не даёт слов для поиска.</div>
            )}
          </div>
        ) : null}
      </section>

      <h2>Синонимы</h2>
      <div className="row">
        <input className="grow" placeholder="слова через запятую: velosiped, велосипед, velik" value={draft} onChange={(e) => setDraft(e.target.value)} />
        <Btn busy={action.busy} disabled={splitWords(draft).length < 2} onClick={() => void add()}>
          Добавить группу
        </Btn>
      </div>
      {groups.isLoading ? <Loading /> : groups.error ? <ErrorBox error={groups.error} onRetry={() => void groups.refetch()} /> : !groups.data?.items.length ? <Empty text="Словарь пуст — используется встроенный" /> : (
        <table>
          <thead><tr><th>Слова</th><th>Включена</th><th>Изменена</th><th /></tr></thead>
          <tbody>
            {groups.data.items.map((g) => (
              <tr key={g.id} className={g.isActive ? undefined : 'muted'}>
                <td>
                  {editing?.id === g.id ? (
                    <input className="grow" value={editing.text} onChange={(e) => setEditing({ id: g.id, text: e.target.value })} />
                  ) : (
                    <>
                      {g.words.join(', ')}
                      {g.multiWord.length ? <div className="muted small">не расширяют запрос (несколько слов): {g.multiWord.join(', ')}</div> : null}
                    </>
                  )}
                </td>
                <td>
                  <input
                    type="checkbox"
                    aria-label={`Группа ${g.words[0]} включена`}
                    checked={g.isActive}
                    onChange={(e) => void action.run(async () => { await api(`/admin/search/synonyms/${g.id}`, { method: 'PATCH', json: { isActive: e.target.checked } }); refresh(); })}
                  />
                </td>
                <td>{fmtDate(g.updatedAt)}</td>
                <td>
                  {editing?.id === g.id ? (
                    <div className="row">
                      <Btn busy={action.busy} onClick={() => void action.run(async () => { await api(`/admin/search/synonyms/${g.id}`, { method: 'PATCH', json: { words: splitWords(editing.text) } }); setEditing(null); refresh(); })}>Сохранить</Btn>
                      <button className="link" onClick={() => setEditing(null)}>отмена</button>
                    </div>
                  ) : (
                    <div className="row">
                      <button className="link" onClick={() => setEditing({ id: g.id, text: g.words.join(', ') })}>изменить</button>
                      <button className="link" onClick={() => void action.run(async () => { if (!window.confirm(`Удалить группу «${g.words.join(', ')}»?`)) return; await api(`/admin/search/synonyms/${g.id}`, { method: 'DELETE' }); refresh(); })}>удалить</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h2>Запросы пользователей</h2>
      <p className="muted small">
        Статистика обезличена: без пользователей и IP; запросы с номерами телефонов не записываются. Учитывается первая страница
        выдачи. Частые запросы без результатов — повод добавить синонимы или категорию.
      </p>
      <div className="toolbar">
        <label className="check"><input type="checkbox" checked={zeroOnly} onChange={(e) => { setZeroOnly(e.target.checked); setPage(1); }} /> только без результатов</label>
        <button className="link" onClick={() => void action.run(async () => { if (!window.confirm('Очистить статистику запросов?')) return; await api('/admin/search/queries', { method: 'DELETE' }); void qc.invalidateQueries({ queryKey: ['search-queries'] }); })}>очистить статистику</button>
      </div>
      {stats.isLoading ? <Loading /> : stats.error ? <ErrorBox error={stats.error} /> : !stats.data?.items.length ? <Empty text="Пока нет данных" /> : (
        <>
          <table>
            <thead><tr><th>Запрос</th><th>Поисков</th><th>Без результатов</th><th>Последний</th><th /></tr></thead>
            <tbody>
              {stats.data.items.map((r) => (
                <tr key={r.key}>
                  <td>{r.sample}<div className="muted small"><code>{r.key}</code></div></td>
                  <td>{r.searches}</td>
                  <td>{r.zeroResults}</td>
                  <td>{fmtDate(r.lastAt)}</td>
                  <td><button className="link" onClick={() => { setDraft(`${r.sample}, `); window.scrollTo({ top: 0 }); }}>в синонимы</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager page={page} pageSize={20} total={stats.data.total} onPage={setPage} />
        </>
      )}
    </div>
  );
}
